import { useEffect, useRef } from 'react'
import { useTOTPTimer } from './TOTPTimerContext'
import { TOTPGenerator } from '@/authenticator/TOTPGenerator'

interface TOTPTimerProps {
  remainingTime?: number // Legacy prop for backward compatibility
  period?: number
  onExpired?: () => void
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  className?: string
}

const TOTPTimer = ({
  remainingTime: legacyRemainingTime,
  period = TOTPGenerator.getPeriod(),
  onExpired,
  size = 'md',
  showText = true,
  className = ''
}: TOTPTimerProps) => {
  const { getRemainingTime } = useTOTPTimer()

  // Use context-based timer or fall back to legacy prop
  const timeLeftFloat = legacyRemainingTime ?? getRemainingTime(period)
  const timeLeft = Math.ceil(timeLeftFloat) // Display whole seconds
  const previousTimeRef = useRef(timeLeft)

  useEffect(() => {
    if (timeLeft === 0 && previousTimeRef.current !== 0) {
      onExpired?.()
    }
    previousTimeRef.current = timeLeft
  }, [timeLeft, onExpired])

  // Use float value for smooth progress animation
  const progress = (timeLeftFloat / period) * 100
  const isUrgent = timeLeft <= 5
  const isWarning = timeLeft <= 10 && timeLeft > 5

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  }

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  }

  // Simple circular progress calculation
  const circumference = 2 * Math.PI * 16
  const strokeDasharray = circumference
  const strokeDashoffset = circumference - (progress / 100) * circumference

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`relative ${sizeClasses[size]} shrink-0`}>
        {/* Simple circular progress timer */}
        <svg
          className="absolute inset-0 -rotate-90"
          width="100%"
          height="100%"
          viewBox="0 0 40 40"
        >
          {/* Background circle */}
          <circle
            cx="20"
            cy="20"
            r="16"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className="text-gray-200"
          />
          {/* Progress circle */}
          <circle
            cx="20"
            cy="20"
            r="16"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className={`${
              isUrgent
                ? 'text-red-500'
                : isWarning
                  ? 'text-yellow-500'
                  : 'text-green-500'
            }`}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>

        {/* Center time display */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={`font-semibold ${
              isUrgent
                ? 'text-red-600'
                : isWarning
                  ? 'text-yellow-600'
                  : 'text-gray-700'
            } ${textSizeClasses[size]}`}
          >
            {timeLeft}
          </span>
        </div>
      </div>

      {showText && (
        <div className="flex flex-col">
          <span
            className={`font-semibold ${
              isUrgent
                ? 'text-red-600'
                : isWarning
                  ? 'text-yellow-600'
                  : 'text-gray-700'
            } ${textSizeClasses[size]}`}
          >
            {timeLeft}s
          </span>
          {size === 'lg' && (
            <span className="text-xs text-gray-500">
              {isUrgent ? 'expires soon' : 'remaining'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default TOTPTimer
